import React from 'react';
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'react-toastify';
import './StampList.css';

const VERIFY_URL = process.env.REACT_APP_VERIFY_URL || 'http://localhost:3000';

function StampList({
  stamps, onEdit, onDelete,
  canEdit = true, canDelete = true,
  page = 1, totalPages = 1, total = 0, pageSize = 10,
  onPageChange,
}) {
  const handleShare = (stamp) => {
    const link = `${VERIFY_URL}/verify?uid=${stamp.unique_id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => toast.success('Verification link copied'));
    } else {
      window.prompt('Copy this verification link:', link);
    }
  };

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end   = Math.min(page * pageSize, total);

  // Build page number buttons (show max 5 around current page)
  const pageButtons = () => {
    const pages = [];
    const delta = 2;
    const left  = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    for (let i = left; i <= right; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="stamp-list-container">
      {stamps.length === 0 ? (
        <div className="no-data">No stamps found</div>
      ) : (
        <>
          <table className="stamp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Stamp Number</th>
                <th>Unique ID</th>
                <th>Applicant Name</th>
                <th>CNIC</th>
                <th>Stamp Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stamps.map((stamp, idx) => (
                <tr key={stamp.id}>
                  <td className="text-small">{start + idx}</td>
                  <td>{stamp.stamp_number}</td>
                  <td className="text-small">{stamp.unique_id}</td>
                  <td>{stamp.applicant_name}</td>
                  <td>{stamp.applicant_cnic}</td>
                  <td>PKR {stamp.stamp_amount}</td>
                  <td>
                    <span className={`badge badge-${stamp.stamp_status}`}>{stamp.stamp_status}</span>
                  </td>
                  <td className="text-small">{new Date(stamp.created_at).toLocaleDateString()}</td>
                  <td>
                    {canEdit && (
                      <button onClick={() => onEdit(stamp)} className="btn btn-sm btn-info" title="Edit">
                        <EditIcon fontSize="small" /> Edit
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => onDelete(stamp.id)} className="btn btn-sm btn-danger" title="Delete">
                        <DeleteIcon fontSize="small" /> Delete
                      </button>
                    )}
                    <button onClick={() => handleShare(stamp)} className="btn btn-sm btn-share" title="Copy link">
                      <ShareIcon fontSize="small" /> Share
                    </button>
                    {stamp.qr_code && (
                      <a href={stamp.qr_code} download={`qr-${stamp.unique_id}.png`}
                        className="btn btn-sm btn-secondary" title="Download QR">
                        <QrCode2Icon fontSize="small" /> QR
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ── Pagination bar ── */}
          <div className="pagination-bar">
            <span className="pagination-info">
              Showing {start}–{end} of {total} stamps
            </span>
            <div className="pagination-controls">
              <button
                className="pg-btn"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                title="Previous"
              >
                <ChevronLeftIcon fontSize="small" />
              </button>

              {page > 3 && (
                <>
                  <button className="pg-btn" onClick={() => onPageChange(1)}>1</button>
                  {page > 4 && <span className="pg-ellipsis">…</span>}
                </>
              )}

              {pageButtons().map((p) => (
                <button
                  key={p}
                  className={`pg-btn ${p === page ? 'pg-btn-active' : ''}`}
                  onClick={() => onPageChange(p)}
                >
                  {p}
                </button>
              ))}

              {page < totalPages - 2 && (
                <>
                  {page < totalPages - 3 && <span className="pg-ellipsis">…</span>}
                  <button className="pg-btn" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
                </>
              )}

              <button
                className="pg-btn"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                title="Next"
              >
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StampList;
